'use client'

import { Box, Button, Chip, Link, Paper, Typography } from '@mui/material'
import type { JSX } from 'react'
import { ReactElement } from 'react'
import { tss } from 'tss-react/mui'
import { AlbatrossTeamIcon } from '../Icon/catalog'
import PlanCard from './PlanCard'

export type TRatePlan = {
    currency: string
    id: string
    name: string
    preselected: boolean
    price: string
    priceFormatted: string
    promotionPageLabel: string
    recurrenceInterval: number | null
    recurrenceIntervalInDays: number | null
    recurrenceIso8601Period: string | null
    recurrencePricePerMonth: string | null
    recurrencePricePerMonthFormatted: string | null
    recurrenceUnit: string | null
    sku: string
    savingPercentage?: number | null
}

const useStyles = tss.withNestedSelectors<'isMostPopular'>().create(({ classes, theme }) => ({
    isMostPopular: {},
    planContainer: {
        padding: theme.spacing(3),
        backgroundColor: 'rgba(30, 112, 161, 0.12)',

        [theme.breakpoints.up('sm')]: {
            borderRadius: '12px',
        },
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(3),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
    },
    cardContainer: {
        display: 'flex',
        flexDirection: 'row',
        paddingBottom: theme.spacing(3),
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0),

        [theme.breakpoints.down('sm')]: {
            paddingBottom: theme.spacing(0),
            marginBottom: theme.spacing(3),
        },
    },
    titleContainer: {
        display: 'flex',
        [`&.${classes.isMostPopular}`]: {
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        margin: 'auto',
        color: theme.palette.primary.dark,
        marginBottom: theme.spacing(3),
    },
    buttonStack: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonIcon: {
        marginRight: theme.spacing(1),
    },
    infoBox: {
        backgroundColor: 'transparent',
        padding: theme.spacing(1),
        marginBottom: theme.spacing(3),
        borderColor: theme.palette.primary.main,
        borderRadius: '6px',
    },
    infoText: {
        color: theme.palette.primary.main,
    },
    linkText: {
        display: 'flex',
        justifyContent: 'center',
        textDecoration: 'none',
        marginTop: theme.spacing(2),
    },
    mostPopularChip: {
        width: '100%',
        position: 'absolute',
        top: '-25px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(30, 112, 161, 0.5)',
        color: theme.palette.common.white,
        borderRadius: '6px 6px 0 0',
    },
    mostPopularContainer: {
        position: 'relative',
    },
    imageWrapper: {
        marginRight: theme.spacing(1),
    },
}))

export type PlanSelectorProps = {
    ratePlans: Array<TRatePlan>
    buttonText: string
    img?: ReactElement
    showInfoBox?: boolean
    buttonIcon?: ReactElement
    buttonLinkText?: string
    showMostPopular?: boolean
    submitAction: () => void
    selectedPlan?: string
    setSelectedPlan: (id: string) => void
}

const PlanSelector = ({
    ratePlans,
    buttonText,
    img,
    showInfoBox = false,
    buttonIcon,
    buttonLinkText,
    showMostPopular = false,
    submitAction,
    selectedPlan,
    setSelectedPlan,
}: PlanSelectorProps): JSX.Element => {
    const { classes, cx } = useStyles()

    return (
        <Box className={classes.planContainer}>
            <Box className={cx(classes.titleContainer, showMostPopular && classes.isMostPopular)}>
                <Typography className={classes.titleText} variant="h3">
                    Choose Your Plan
                </Typography>
            </Box>
            <Box>
                <Box className={classes.cardContainer}>
                    {ratePlans.map((plan, index) => (
                        <Box key={index} className={classes.mostPopularContainer}>
                            {showMostPopular && plan.preselected ? (
                                <Chip className={classes.mostPopularChip} label="Most Popular" size="small" />
                            ) : null}
                            <PlanCard
                                isMostPopular={showMostPopular ? plan.preselected : false}
                                key={index}
                                plan={plan}
                                selectedPlan={selectedPlan}
                                setSelectedPlan={setSelectedPlan}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
            {showInfoBox ? (
                <Paper className={classes.infoBox}>
                    <Box className={classes.buttonStack}>
                        <Box className={classes.imageWrapper}>{img}</Box>
                        <Box>
                            <Typography variant="subtitle2" className={classes.infoText}>
                                14-day money back guarantee
                            </Typography>
                            <Typography variant="subtitle2" className={classes.infoText}>
                                Cancel anytime
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            ) : null}
            <Button variant="contained" fullWidth onClick={submitAction}>
                {buttonIcon ? <AlbatrossTeamIcon className={classes.buttonIcon} /> : null}
                {buttonText}
            </Button>
            <Link className={classes.linkText}>{buttonLinkText}</Link>
        </Box>
    )
}

export default PlanSelector
